import { useCallback, useMemo } from "react";
import useActiveProfile from "./useActiveProfile";
import { getPlanLimit } from "../../../util/subscription";
import useTranslation from "../../../i18n/useTranslation";

function normalizePlanName(subscription) {
    const raw = subscription?.plan?.name;
    return raw ? String(raw).trim().toLowerCase() : "";
}

function getPlanKeyForLimits(subscription) {
    const name = normalizePlanName(subscription);

    if (!name || name === "free") return "free";
    if (name.includes("enterprise")) return "enterprise";
    if (name.includes("elite")) return "elite";
    if (name.includes("trial")) return "trial";
    if (name.includes("pro")) return "pro";

    return "free";
}

export default function useCheckOrganizationLimitOrPrompt() {
    const { activeProfile, authData } = useActiveProfile();
    const {t} = useTranslation();

    const organizationEntitlement = useMemo(() => {
        const profileOrganizations = activeProfile?.profileOrganizations ?? [];
        const userId = String(authData?.user?.id || "");
        const ownedOrganizations = profileOrganizations
            .map((profileOrganization) => profileOrganization?.organization)
            .filter((organization) => (
                organization?.createdBy?.id &&
                String(organization.createdBy.id) === userId
            ));

        const limit = ownedOrganizations.reduce((total, organization) => {
            const planLimit = getPlanLimit(
                getPlanKeyForLimits(organization.activeSubscription),
                "organizations"
            );
            return total + (planLimit > 1 ? planLimit : 0);
        }, 0);

        return {
            count: ownedOrganizations.length,
            limit: limit || 1,
        };
    }, [activeProfile, authData]);

    return useCallback(
        ({ currentCount, currentLimit, itemLabel = "organizations", toast }) => {
            const { count: cachedCount, limit } = organizationEntitlement;
            const count = Number.isFinite(currentCount) ? currentCount : cachedCount;
            const effectiveLimit = Number.isFinite(currentLimit) ? currentLimit : limit;

            if (count >= effectiveLimit) {
                toast.warning(
                    t("subscriptionLimit.organization_limit_reached", {
                        limit: effectiveLimit,
                        item: itemLabel,
                    }),
                    {
                        autoClose: 5000,
                        toastId: "organization_subscription_restriction",
                        position: "top-center",
                        closeOnClick: true,
                    }
                );
                return false;
            }

            return true;
        },
        [organizationEntitlement, t]
    );
}
